import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api/useApi';
import { ApiClient } from '../api/ApiClient';

export interface PaginatedResponse<T> {
  items: T[];
  nextCursor: string | null;
}

export interface UsePaginatedResourceResult<T> {
  items: T[];
  loading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

export function usePaginatedResource<T>(
  key: ReadonlyArray<unknown>,
  fetcher: (api: ApiClient, cursor: string | undefined) => Promise<PaginatedResponse<T>>,
): UsePaginatedResourceResult<T> {
  const api = useApi();
  const [pages, setPages] = useState<Array<PaginatedResponse<T>>>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const query = useQuery<PaginatedResponse<T>, Error>({
    queryKey: [...key, cursor],
    queryFn: async () => {
      const page = await fetcher(api, cursor);
      setPages((prev) => {
        if (cursor === undefined) return [page];
        return [...prev, page];
      });
      return page;
    },
  });

  const items = pages.flatMap((p) => p.items);
  const hasMore = pages.length > 0 && pages[pages.length - 1]?.nextCursor !== null;

  const loadMore = useCallback(() => {
    const last = pages[pages.length - 1];
    if (last?.nextCursor) {
      setCursor(last.nextCursor);
    }
  }, [pages]);

  const refetch = useCallback(() => {
    setPages([]);
    setCursor(undefined);
    void query.refetch();
  }, [query]);

  return {
    items,
    loading: query.isLoading || query.isFetching,
    error: query.error ?? null,
    hasMore,
    loadMore,
    refetch,
  };
}
